import { Route, Routes, Navigate } from 'react-router-dom';
import Register from '@/pages/Auth/Register';
import Login from '@/pages/Auth/Login';
import Home from '@/pages/Home/Home';
import { OccupancyPanel } from '@/pages/OccupancyPanel/OccupancyPanel';
import { CrmPageNew } from '@/pages/CrmPage/CrmPageNew';
import ProtectedRoute from '@/routes/ProtectedRoute';
import { AccountsToReceive } from '@/pages/AccountsToReceive/AccountsToReceive';
import { FinancialDefault } from '@/pages/FinancialDefault/FinancialDefault.tsx';
import { Dashboard } from '@/pages/Dashboard/Dashboard.tsx';
import { CrmDashboard } from '@/pages/CrmPage/CrmDashboard';
import { TypesCRM } from '@/types/crm.ts';
import { InadimplenciaVisaoHistorica } from '@/pages/InadimplenciaVisaoHistorica/InadimplenciaVisaoHistorica';
import { InadimplenciaPage } from '@/pages/InadimplenciaPage/InadimplenciaPage';
import { InadimplenciaConfiguracoesAvancadas } from '@/pages/InadimplenciaConfiguracoesAvancadas/InadimplenciaConfiguracoesAvancadas';
import { InadimplenciaHistoricoPercentual } from '@/pages/InadimplenciaHistoricoPercentual/InadimplenciaHistoricoPercentual';
import { InadimplenciaEvolucao } from '@/pages/InadimplenciaEvolucao/InadimplenciaEvolucao';
import { RecomendacoesAlunos } from '@/pages/RecomendacoesAlunos/RecomendacoesAlunos';
import { InadimplenciaConfiguracoesAvancadasEvolucao } from '@/pages/InadimplenciaConfiguracoesAvancadasEvolucao/InadimplenciaConfiguracoesAvancadasEvolucao';
import { TermometroGeralAlunos } from '@/pages/TermometroGeralAlunos/TermometroGeralAlunos';
import TermometroFinanceiro from '@/pages/TermometroFinanceiro/TermometroFinanceiro';
import { TermometroFrequencia } from '@/pages/TermometroFrequencia/TermometroFrequencia';
import { TermometroRaiox } from '@/pages/TermometroRaiox/TermometroRaiox';
import { TermometroConfiguracoesAvancadas } from '@/pages/TermometroConfiguracoesAvancadas/TermometroConfiguracoesAvancadas';
import { TermometroHistoricoGeral } from '@/pages/TermometroHistoricoGeral/TermometroHistoricoGeral';
import { TermometroFrequenciaAlunos } from '@/pages/TermometroFrequenciaAlunos/TermometroFrequenciaAlunos';
import { TermometroConfiguracoesAvancadasFrequencia } from '@/pages/TermometroConfiguracoesAvancadasFrequencia/TermometroConfiguracoesAvancadasFrequencia';
import TermometroGeral from '@/pages/TermometroGeral/TermometroGeral';
import { TermometroConfiguracoesAvancadasGeral } from '@/pages/TermometroConfiguracoesAvancadasGeral/TermometroConfiguracoesAvancadasGeral';
import TermometroPedagogico from '@/pages/TermometroPedagogico/TermometroPedagogico';
import { TermometroPedagogicolAlunos } from '@/pages/TermometroPedagogicolAlunos/TermometroPedagogicolAlunos';
import { TermometroConfiguracoesAvancadasPedagogico } from '@/pages/TermometroConfiguracoesAvancadasPedagogico/TermometroConfiguracoesAvancadasPedagogico';
import { TermometroHistoricoPedagogico } from '@/pages/TermometroHistoricoPedagogico/TermometroHistoricoPedagogico';
import { TermometroHistoricoFinanceiro } from '@/pages/TermometroHistoricoFinanceiro/TermometroHistoricoFinanceiro';
import { TermometroGeralAlunosFinanceiro } from '@/pages/TermometroGeralAlunosFinanceiro/TermometroGeralAlunosFinanceiro';
import { TermometroHistoricoFrequencia } from '@/pages/TermometroHistoricoFrequencia/TermometroHistoricoFrequencia.tsx';
import IndiceValor from '@/features/dashboard/IndiceValor';
import { TableHeaderCustomIndice } from '@/components/Base/TableHeaderCustomIndice';
import CashFlowTela from '@/features/dashboard/CashFlowTela';
import RaioX from '@/features/dashboard/RaioX';
import { TermometroRaioxAluno } from '@/pages/TermometroRaioxAluno/TermometroRaioxAluno';
import { RecomendacoesAlunosRaiox } from '@/pages/RecomendacoesAlunosRaiox/RecomendacoesAlunosRaiox';
import RaioXProfessores from '@/features/dashboard/RaioXProfessores';
import RaioXInstituicao from '@/features/dashboard/RaioXInstituicao';
import RaioXTurmas from '@/features/dashboard/RaioXTurmas';
import ScorePedagogico from '@/pages/ScorePedagogico/ScorePedagogico';
import { useUserStore } from '@/stores/user';
import { listPermissions, getDefaultRouteForUser } from '@/utils/routePermissions';

type PermissionType = {
  route: string;
  container: any;
  props?: any;
  permission?: string;
}

const getElementRoute = (permission: PermissionType) => {
  const Container = permission.container;
  return (
    <Route
      key={permission.route}
      path={permission.route}
      element={
        <ProtectedRoute>
          <Container {...permission?.props} />
        </ProtectedRoute>
      }
    />
  )
}

// Componente para verificar se o usuário tem permissão para acessar uma rota específica
const PermissionProtectedRoute = ({ children, requiredPermission, permissions }: { 
  children: React.ReactNode, 
  requiredPermission?: string, 
  permissions: string[] 
}) => {
  if (!requiredPermission) return <>{children}</>;
  
  const hasAccess = permissions.length === 0 || permissions.some((perm) => perm.includes(requiredPermission));
  
  if (!hasAccess) {
    return <Navigate to="/" replace />;
  }
  
  return <>{children}</>;
};

const Routing = () => {
  const { user, hasPermission } = useUserStore()
  const permissions = user?.permissions || []

  // Aplica a mesma lógica de filtro do menu - só permite páginas que o usuário tem acesso
  const allowedPermissions = Object.keys(listPermissions).filter((key) => 
    permissions.length === 0 || permissions.some((perm) => perm.includes(key))
  );

  // Define a rota principal baseada na primeira permissão permitida
  const getDefaultRoute = () => {
    if (permissions.length === 0) {
      return Dashboard; // Se não tem permissões específicas, mostra dashboard
    }
    
    // Pega a primeira permissão permitida
    const firstAllowedPermission = allowedPermissions[0];
    if (firstAllowedPermission && listPermissions[firstAllowedPermission]) {
      return listPermissions[firstAllowedPermission].container;
    }
    
    return Dashboard; // fallback para dashboard
  };

  // Usa getDefaultRouteForUser para determinar tanto a rota quanto o container
  const defaultRoute = getDefaultRouteForUser(permissions);
  const homeRoute = {
    route: defaultRoute,
    container: getDefaultRoute()
  };


  return (
    <Routes>
      <Route path="/login" element={<Login />} />
      <Route path="/register" element={<Register />} />

      {/* Rota principal */}
      {/* {getElementRoute(homeRoute)} */}

      {/* Rota explícita para dashboard */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="dashboard" permissions={permissions}>
              <Dashboard />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Rotas explícitas para páginas principais */}
      <Route
        path="/inadimplencia"
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="inadimplencia" permissions={permissions}>
              <InadimplenciaPage />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/termometro-geral"
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="termometro" permissions={permissions}>
              <TermometroGeral />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path="/occupancy-panel"
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="painel_ocupacao" permissions={permissions}>
              <OccupancyPanel routeBack="/dashboard" />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      {/* Rotas baseadas em permissões permitidas */}
      {
        allowedPermissions.map((key) => {
          const permission = listPermissions[key];
          const explicitRoutes = ['/dashboard', '/inadimplencia', '/termometro-geral', '/occupancy-panel'];
          if (explicitRoutes.includes(permission.route)) {
            return null;
          }
          return getElementRoute(permission)
        }).filter(Boolean)
      }

      <Route
        path={'/crm/matricula'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="crm" permissions={permissions}>
              <CrmPageNew title={'CRM Matrícula'} type={TypesCRM.MATRICULA} />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/crm/rematricula'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="crm" permissions={permissions}>
              <CrmPageNew title={'CRM Rematrícula'} type={TypesCRM.REMATRICULA} />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/crm/inadimplencia'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="crm" permissions={permissions}>
              <CrmPageNew title={'CRM Inadimplência'} type={TypesCRM.INADIMPLENCIA} />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/crm/dashboard'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="crm" permissions={permissions}>
              <CrmDashboard title={'CRM - Dashboard'} />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/financial-default'}
        element={
          <ProtectedRoute>
            <FinancialDefault />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-configuracoes-avancadas'}
        element={
          <ProtectedRoute>
            <InadimplenciaConfiguracoesAvancadas routeBack={'/inadimplencia'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-configuracoes-avancadas-visao'}
        element={
          <ProtectedRoute>
            <InadimplenciaConfiguracoesAvancadas routeBack={'/inadimplencia-visao-historica'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-configuracoes-avancadas-evolucao'}
        element={
          <ProtectedRoute>
            <InadimplenciaConfiguracoesAvancadasEvolucao routeBack={'/inadimplencia-evolucao'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-visao-historica'}
        element={
          <ProtectedRoute>
            <InadimplenciaVisaoHistorica />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-historico-percentual'}
        element={
          <ProtectedRoute>
            <InadimplenciaHistoricoPercentual backRoute={'/inadimplencia-configuracoes-avancadas'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-historico-percentual-visao'}
        element={
          <ProtectedRoute>
            <InadimplenciaHistoricoPercentual backRoute={'/inadimplencia-configuracoes-avancadas-visao'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-evolucao'}
        element={
          <ProtectedRoute>
            <InadimplenciaEvolucao />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/inadimplencia-recomendacoes-alunos'}
        element={
          <ProtectedRoute>
            <RecomendacoesAlunos />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-financeiro'}
        element={
          <ProtectedRoute>
            <TermometroFinanceiro />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-financeiro-raiox'}
        element={
          <ProtectedRoute>
            <TermometroRaiox />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-frequencia-raiox'}
        element={
          <ProtectedRoute>
            <TermometroRaiox />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-geral-aluno-financeiro'}
        element={
          <ProtectedRoute>
            <TermometroGeralAlunosFinanceiro routeBack={'/termometro-financeiro'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/configuracoes-avancadas-termometro-financeiro'}
        element={
          <ProtectedRoute>
            <TermometroConfiguracoesAvancadas routeBack={'/termometro-financeiro'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/configuracoes-avancadas-termometro-frequencia'}
        element={
          <ProtectedRoute>
            <TermometroConfiguracoesAvancadasFrequencia routeBack={'/termometro-frequencia'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-historico-geral'}
        element={
          <ProtectedRoute>
            <TermometroHistoricoGeral backRoute={'/configuracoes-avancadas-termometro-financeiro'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-geral-raiox'}
        element={
          <ProtectedRoute>
            <TermometroRaiox />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-geral-aluno'}
        element={
          <ProtectedRoute>
            <TermometroGeralAlunos routeBack={'/termometro-geral'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/configuracoes-avancadas-termometro-geral'}
        element={
          <ProtectedRoute>
            <TermometroConfiguracoesAvancadasGeral routeBack={'/termometro-geral'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-pedagogico'}
        element={
          <ProtectedRoute>
            <TermometroPedagogico />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-pedagogico-raiox'}
        element={
          <ProtectedRoute>
            <TermometroRaiox />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-pedagogico-aluno'}
        element={
          <ProtectedRoute>
            <TermometroPedagogicolAlunos routeBack={'/termometro-pedagogico'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/configuracoes-avancadas-termometro-pedagogico'}
        element={
          <ProtectedRoute>
            <TermometroConfiguracoesAvancadasPedagogico routeBack={'/termometro-pedagogico'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-historico-pedagogico'}
        element={
          <ProtectedRoute>
            <TermometroHistoricoPedagogico backRoute={'/configuracoes-avancadas-termometro-pedagogico'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-historico-financeiro'}
        element={
          <ProtectedRoute>
            <TermometroHistoricoFinanceiro backRoute={'/configuracoes-avancadas-termometro-financeiro'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-frequencia/aluno/detalhe/:id'}
        element={
          <ProtectedRoute>
            <TermometroFrequenciaAlunos routeBack={'/termometro-frequencia'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-frequencia'}
        element={
          <ProtectedRoute>
            <TermometroFrequencia routeBack={'/termometro-frequencia'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/termometro-historico-frequencia'}
        element={
          <ProtectedRoute>
            <TermometroHistoricoFrequencia backRoute={'/configuracoes-avancadas-termometro-frequencia'} />
          </ProtectedRoute>
        }
      />

      <Route
        path={'/fluxo-caixa'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="fluxo_caixa" permissions={permissions}>
              <CashFlowTela />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/indice-configuracoes-avancadas'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="indice_valor" permissions={permissions}>
              <TableHeaderCustomIndice routeBack={'/indice-valor'} />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/raiox-termometro-alunos'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="raixo_x" permissions={permissions}>
              <TermometroRaioxAluno />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path={'/recomendacoes-alunos-raiox'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="raixo_x" permissions={permissions}>
              <RecomendacoesAlunosRaiox />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path={'/raiox-professores'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="raixo_x" permissions={permissions}>
              <RaioXProfessores />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />
      <Route
        path={'/raiox-instituicoes'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="raixo_x" permissions={permissions}>
              <RaioXInstituicao />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />
       <Route
        path={'/raiox-turmas'}
        element={
          <ProtectedRoute>
            <PermissionProtectedRoute requiredPermission="raixo_x" permissions={permissions}>
              <RaioXTurmas />
            </PermissionProtectedRoute>
          </ProtectedRoute>
        }
      />

      <Route
        path={'/score-pedagogico'}
        element={
          <ProtectedRoute>
            <ScorePedagogico />
          </ProtectedRoute>
        }
      />

      {/* Rota de fallback - deve ser a última */}
      <Route path="*" element={<Home />} />
    </Routes>
  );
};

export default Routing;
